import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { rupiah, todayISO } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Download, MessageCircle, Save, Ticket, Search, UserPlus, Users, Mail } from "lucide-react";

const EMPTY = {
  invoice_date: todayISO(),
  customer_name: "", customer_id: null, phone: "", email: "",
  booking_source: "Agoda", airline: "", pnr: "", route: "", passenger: "",
  ticket_price: 0, service_fee: 0, other_fee: 0, cashback: 0, pax_count: 1,
  payment_status: "Unpaid", payment_method: "", notes: "",
  eticket_ids: [], eticket_prices: {}, ota_buy_prices: {},
};

function CustomerPicker({ value, onPick, onOpenCreate }) {
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => { api.get("/customers").then(r => setCustomers(r.data)); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return customers.slice(0, 30);
    return customers.filter(c => (c.name + " " + (c.phone || "") + " " + (c.email || "")).toLowerCase().includes(s)).slice(0, 30);
  }, [customers, q]);

  return (
    <div className="relative">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="customer-search"
            placeholder={value?.name || "Cari customer terdaftar..."}
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 160)}
            className="pl-9"
          />
        </div>
        <Button type="button" variant="outline" onClick={onOpenCreate} data-testid="customer-add-new">
          <UserPlus size={14} className="mr-1"/> Baru
        </Button>
      </div>
      {open && (
        <div className="absolute z-40 mt-1 w-full max-h-64 overflow-auto bg-white border border-slate-200 rounded-lg shadow-lg">
          {filtered.length === 0 && (
            <div className="p-4 text-center text-sm text-slate-400">Tidak ditemukan. Klik "Baru" untuk menambah.</div>
          )}
          {filtered.map((c) => (
            <button
              key={c.id}
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-[#f0f2e6] border-b border-slate-50"
              data-testid={`cust-opt-${c.id}`}
              onClick={() => { onPick(c); setQ(""); setOpen(false); }}
            >
              <div className="font-semibold text-slate-900">{c.name}</div>
              <div className="text-xs text-slate-500">{c.phone || "-"} · {c.email || "-"} · {c.total_transactions || 0} trx</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function InvoiceForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [saved, setSaved] = useState(null);
  const [etickets, setEtickets] = useState([]);
  const [etQuery, setEtQuery] = useState("");
  const [addCust, setAddCust] = useState(null);  // {name, phone, email} when creating

  useEffect(() => {
    api.get("/etickets").then((r) => setEtickets(r.data));
    if (id) api.get(`/invoices/${id}`).then((r) => {
      setForm({ ...EMPTY, ...r.data, eticket_prices: r.data.eticket_prices || {} });
      setSaved(r.data);
    });
  }, [id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPrice = (etId, v) => setForm((f) => ({ ...f, eticket_prices: { ...(f.eticket_prices || {}), [etId]: v } }));

  const selectedEts = useMemo(
    () => etickets.filter((e) => (form.eticket_ids || []).includes(e.id)),
    [etickets, form.eticket_ids]
  );

  // compute totals
  const useLineItems = selectedEts.length > 0;
  const linesTotal = useLineItems
    ? selectedEts.reduce((sum, e) => {
        const price = Number(form.eticket_prices?.[e.id] || 0);
        const pax = Math.max(1, e.passengers?.length || 1);
        return sum + price * pax;
      }, 0)
    : Number(form.ticket_price || 0) * Math.max(1, Number(form.pax_count || 1));
  const subtotal = linesTotal + Number(form.service_fee || 0) + Number(form.other_fee || 0);
  const total = Math.max(0, subtotal - Number(form.cashback || 0));

  const filteredEts = useMemo(() => {
    const q = etQuery.trim().toLowerCase();
    if (!q) return etickets;
    return etickets.filter((e) =>
      [e.eticket_number, e.passenger, e.pnr, e.airline_ref, e.airline, e.route]
        .filter(Boolean).some((x) => String(x).toLowerCase().includes(q))
    );
  }, [etickets, etQuery]);

  const toggleEt = (etId, checked) => {
    setForm((f) => {
      const next = new Set(f.eticket_ids || []);
      if (checked) next.add(etId); else next.delete(etId);
      const arr = Array.from(next);
      const chosen = etickets.filter((e) => arr.includes(e.id));
      const names = Array.from(new Set(
        chosen.flatMap((e) => (e.passengers?.length ? e.passengers.map((p) => p.name).filter(Boolean) : [e.passenger].filter(Boolean)))
      ));
      const pnrs = Array.from(new Set(chosen.map((e) => e.airline_ref || e.pnr).filter(Boolean)));
      const airlines = Array.from(new Set(
        chosen.flatMap((e) => (e.flights?.length ? e.flights.map((fl) => fl.carrier).filter(Boolean) : [e.airline].filter(Boolean)))
      ));
      const routes = chosen.flatMap((e) => (e.flights?.length ? e.flights.map((fl) => `${fl.from_airport} → ${fl.to_airport}`) : [e.route])).filter(Boolean);
      const nextPrices = { ...(f.eticket_prices || {}) };
      if (checked && !(etId in nextPrices)) nextPrices[etId] = 0;
      if (!checked) delete nextPrices[etId];
      return {
        ...f,
        eticket_ids: arr,
        passenger: names.join(", "),
        pnr: pnrs.join(", "),
        airline: airlines.join(", "),
        route: routes.slice(0, 2).join(" · "),
        eticket_prices: nextPrices,
      };
    });
  };

  const pickCustomer = (c) => setForm((f) => ({ ...f, customer_id: c.id, customer_name: c.name, phone: c.phone || "", email: c.email || "" }));

  const submitAddCust = async () => {
    if (!addCust?.name) return toast.error("Nama wajib");
    try {
      const { data } = await api.post("/customers", addCust);
      toast.success("Customer terdaftar");
      pickCustomer(data);
      setAddCust(null);
    } catch { toast.error("Gagal daftar customer"); }
  };

  const save = async () => {
    if (!form.customer_name) return toast.error("Pilih atau daftarkan customer");
    try {
      const payload = {
        ...form,
        ticket_price: Number(form.ticket_price || 0),
        service_fee: Number(form.service_fee || 0),
        other_fee: Number(form.other_fee || 0),
        cashback: Number(form.cashback || 0),
        pax_count: Math.max(1, Number(form.pax_count || 1)),
        eticket_prices: Object.fromEntries(Object.entries(form.eticket_prices || {}).map(([k, v]) => [k, Number(v || 0)])),
      };
      const r = saved ? await api.put(`/invoices/${saved.id}`, payload) : await api.post("/invoices", payload);
      setSaved(r.data);
      toast.success("Invoice tersimpan");
      if (!id) nav(`/invoices/${r.data.id}`, { replace: true });
    } catch { toast.error("Gagal menyimpan"); }
  };

  const pdfUrl = saved ? `${process.env.REACT_APP_BACKEND_URL}/api/invoices/${saved.id}/pdf?token=${localStorage.getItem("gg_token")}` : "#";
  const publicUrl = saved?.access_token ? `${window.location.origin}/i/${saved.id}?t=${saved.access_token}` : "";
  const publicReceiptUrl = saved?.access_token
    ? `${process.env.REACT_APP_BACKEND_URL}/api/public/receipt/${saved.id}/pdf?t=${saved.access_token}`
    : (saved ? `${process.env.REACT_APP_BACKEND_URL}/api/invoices/${saved.id}/receipt.pdf?token=${localStorage.getItem("gg_token")}` : "");

  const waSend = () => {
    if (!saved) return toast.error("Simpan invoice terlebih dahulu");
    const phone = (form.phone || "").replace(/[^0-9]/g, "");
    const msg = encodeURIComponent(
      `Halo ${form.customer_name},\n\nBerikut invoice ${saved.invoice_number} sebesar ${rupiah(saved.total)}.\n${publicUrl ? "Link e-ticket & invoice: " + publicUrl + "\n" : "Unduh PDF: " + pdfUrl + "\n"}\nTerima kasih — Gogojalan`
    );
    window.open(`https://wa.me/${phone}?text=${msg}`, "_blank");
  };

  const emailSend = () => {
    if (!saved) return toast.error("Simpan invoice terlebih dahulu");
    const subj = encodeURIComponent(`Invoice ${saved.invoice_number} — Gogojalan`);
    const body = encodeURIComponent(
      `Halo ${form.customer_name},\n\nBerikut invoice ${saved.invoice_number} sebesar ${rupiah(saved.total)}.\n${publicUrl ? "Link e-ticket & invoice: " + publicUrl + "\n" : "Unduh PDF: " + pdfUrl + "\n"}\nTerima kasih — Gogojalan`
    );
    window.location.href = `mailto:${form.email || ""}?subject=${subj}&body=${body}`;
  };

  const waSendReceipt = () => {
    if (!saved) return toast.error("Simpan invoice terlebih dahulu");
    const phone = (form.phone || "").replace(/[^0-9]/g, "");
    const msg = encodeURIComponent(
      `Halo ${form.customer_name},\n\nTerima kasih 🙏\nPembayaran invoice ${saved.invoice_number} sebesar ${rupiah(saved.total)} telah kami terima dengan LUNAS.\n\nBukti pembayaran resmi:\n${publicReceiptUrl}\n\nSalam,\nGogojalan`
    );
    window.open(`https://wa.me/${phone}?text=${msg}`, "_blank");
  };

  const emailSendReceipt = () => {
    if (!saved) return toast.error("Simpan invoice terlebih dahulu");
    const subj = encodeURIComponent(`Bukti Pembayaran ${saved.invoice_number} — Gogojalan`);
    const body = encodeURIComponent(
      `Halo ${form.customer_name},\n\nTerima kasih. Pembayaran invoice ${saved.invoice_number} sebesar ${rupiah(saved.total)} telah kami terima dengan LUNAS.\n\nBukti pembayaran resmi bisa diunduh di sini:\n${publicReceiptUrl}\n\nSalam,\nGogojalan`
    );
    window.location.href = `mailto:${form.email || ""}?subject=${subj}&body=${body}`;
  };

  return (
    <div className="space-y-6" data-testid="invoice-form">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">{saved ? `Invoice ${saved.invoice_number}` : "Create Invoice"}</h1>
          <p className="text-slate-500 mt-1 text-sm sm:text-base">Pilih customer, lalu pilih satu atau beberapa e-ticket — setiap e-ticket punya harga terpisah.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {saved && <a href={pdfUrl} target="_blank" rel="noreferrer"><Button variant="outline" data-testid="invoice-pdf-btn"><Download size={14} className="mr-1"/> PDF</Button></a>}
          {saved && <Button variant="outline" onClick={waSend} data-testid="invoice-wa-btn"><MessageCircle size={14} className="mr-1 text-[#25D366]"/> WhatsApp</Button>}
          {saved && <Button variant="outline" onClick={emailSend} data-testid="invoice-email-btn"><Mail size={14} className="mr-1"/> Email</Button>}
          {saved?.payment_status === "Paid" && (
            <>
            <Button variant="outline" onClick={waSendReceipt} data-testid="invoice-wa-receipt-btn" className="border-[#556b2f] text-[#3d4d1e]"><MessageCircle size={14} className="mr-1 text-[#3d4d1e]"/> WA Bukti Bayar</Button>
            <Button variant="outline" onClick={emailSendReceipt} data-testid="invoice-email-receipt-btn" className="border-[#556b2f] text-[#3d4d1e]"><Mail size={14} className="mr-1 text-[#3d4d1e]"/> Email Bukti Bayar</Button>
            </>
          )}
          <Button onClick={save} className="bg-[#556b2f] hover:bg-[#3d4d1e]" data-testid="invoice-save-btn"><Save size={14} className="mr-1"/> Save</Button>
        </div>
      </div>

      {/* Per-invoice margin card (internal only) */}
      {useLineItems && (
        <Card className="border-amber-200">
          <CardContent className="pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Harga Jual</div>
                <div className="text-xl font-extrabold mono text-[#556b2f] mt-1">{rupiah(total)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Total Beli OTA</div>
                <div className="text-xl font-extrabold mono text-amber-700 mt-1">
                  {rupiah(selectedEts.reduce((s, e) => s + Number(form.ota_buy_prices?.[e.id] || 0) * Math.max(1, e.passengers?.length || 1), 0))}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Gross Margin</div>
                <div className="text-xl font-extrabold mono text-[#6b8e23] mt-1">
                  {rupiah(total - selectedEts.reduce((s, e) => s + Number(form.ota_buy_prices?.[e.id] || 0) * Math.max(1, e.passengers?.length || 1), 0))}
                </div>
              </div>
            </div>
            <p className="text-[10px] text-slate-400 text-center mt-2">Info internal — tidak dicetak di invoice customer</p>
          </CardContent>
        </Card>
      )}

      {/* Customer picker */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Users size={18} className="text-[#6b8e23]"/> Customer</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {form.customer_name && !addCust && (
            <div className="p-3 rounded-lg bg-[#f0f2e6] border border-[#e4ecc9] flex items-center justify-between" data-testid="picked-customer">
              <div>
                <div className="font-semibold text-slate-900">{form.customer_name}</div>
                <div className="text-xs text-slate-500">{form.phone || "-"} · {form.email || "-"}</div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setForm(f => ({...f, customer_id: null, customer_name: "", phone: "", email: ""}))}>Ganti</Button>
            </div>
          )}
          {!form.customer_name && !addCust && (
            <CustomerPicker value={form.customer_id ? {name: form.customer_name} : null} onPick={pickCustomer} onOpenCreate={() => setAddCust({name:"", phone:"", email:""})} />
          )}
          {addCust && (
            <div className="p-4 rounded-lg border border-[#e4ecc9] bg-[#fafcf3] space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div><Label>Nama *</Label><Input value={addCust.name} onChange={e => setAddCust({...addCust, name: e.target.value})} data-testid="new-cust-name"/></div>
                <div><Label>Phone</Label><Input value={addCust.phone} onChange={e => setAddCust({...addCust, phone: e.target.value})} placeholder="6281..."/></div>
                <div><Label>Email</Label><Input value={addCust.email} onChange={e => setAddCust({...addCust, email: e.target.value})}/></div>
              </div>
              <div className="flex gap-2">
                <Button onClick={submitAddCust} className="bg-[#556b2f] hover:bg-[#3d4d1e]" data-testid="new-cust-save"><UserPlus size={14} className="mr-1"/> Daftar & Pilih</Button>
                <Button variant="ghost" onClick={() => setAddCust(null)}>Batal</Button>
              </div>
            </div>
          )}
          <p className="text-xs text-slate-400">Nama customer bisa berbeda dari nama penumpang. Penumpang otomatis diambil dari e-ticket yang dipilih.</p>
        </CardContent>
      </Card>

      {/* E-ticket picker */}
      <Card className="border-[#e4ecc9]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Ticket size={18} className="text-[#6b8e23]"/> Pilih E-ticket ({(form.eticket_ids || []).length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 mb-3">
            <Search size={14} className="text-slate-400"/>
            <Input placeholder="Cari nomor / passenger / PNR..." value={etQuery} onChange={(e) => setEtQuery(e.target.value)} className="max-w-sm" data-testid="et-picker-search"/>
            <span className="text-xs text-slate-400 ml-auto">{filteredEts.length} e-ticket tersedia</span>
          </div>
          <div className="max-h-64 overflow-auto border border-slate-100 rounded-lg divide-y">
            {filteredEts.length === 0 && <div className="p-6 text-center text-slate-400 text-sm">Tidak ada e-ticket. Terbitkan dulu di menu E-Ticket / Booking OTA.</div>}
            {filteredEts.map((e) => {
              const checked = (form.eticket_ids || []).includes(e.id);
              const names = e.passengers?.length ? e.passengers.map((p) => p.name).filter(Boolean).join(", ") : e.passenger || "-";
              const paxCount = e.passengers?.length || 1;
              return (
                <label key={e.id} className={`flex items-start gap-3 p-3 hover:bg-slate-50 cursor-pointer ${checked ? "bg-[#f0f2e6]" : ""}`}>
                  <Checkbox checked={checked} onCheckedChange={(v) => toggleEt(e.id, !!v)} data-testid={`et-check-${e.eticket_number}`} className="mt-0.5" />
                  <div className="flex-1 grid grid-cols-3 gap-2 text-sm">
                    <div>
                      <div className="mono font-semibold text-slate-900">{e.eticket_number}</div>
                      <div className="text-[10px] text-slate-400">PNR {e.airline_ref || e.pnr || "-"} · {paxCount} pax</div>
                    </div>
                    <div className="col-span-2">
                      <div className="font-medium text-slate-800">{names}</div>
                      <div className="text-xs text-slate-500">{e.flights?.[0]?.carrier || e.airline || "-"} · {e.flights?.length || 1} segmen</div>
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Per-eticket pricing */}
      {useLineItems && (
        <Card>
          <CardHeader><CardTitle>Harga per E-ticket</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {selectedEts.map((e) => {
              const names = e.passengers?.length ? e.passengers.map((p) => p.name).filter(Boolean).join(", ") : e.passenger;
              const pax = Math.max(1, e.passengers?.length || 1);
              const price = Number(form.eticket_prices?.[e.id] || 0);
              return (
                <div key={e.id} className="grid grid-cols-2 sm:grid-cols-12 gap-3 items-end p-3 rounded-lg bg-slate-50 border border-slate-100">
                  <div className="col-span-2 sm:col-span-5">
                    <div className="mono text-xs text-slate-500">{e.eticket_number}</div>
                    <div className="font-semibold text-slate-900 text-sm">{names}</div>
                    <div className="text-xs text-slate-500">{e.flights?.[0]?.carrier} · {e.flights?.[0]?.from_airport?.match(/\(([A-Z]{3})\)/)?.[1] || e.flights?.[0]?.from_airport} → {e.flights?.[e.flights.length-1]?.to_airport?.match(/\(([A-Z]{3})\)/)?.[1] || e.flights?.[e.flights.length-1]?.to_airport}</div>
                  </div>
                  <div className="col-span-1 sm:col-span-3">
                    <Label className="text-xs">Harga / Pax</Label>
                    <Input type="number" value={price} onChange={(ev) => setPrice(e.id, ev.target.value)} data-testid={`et-price-${e.eticket_number}`}/>
                  </div>
                  <div className="col-span-1 sm:col-span-2">
                    <Label className="text-xs text-slate-500">Beli OTA / Pax</Label>
                    <Input type="number" value={form.ota_buy_prices?.[e.id] || 0} onChange={(ev) => setForm(f => ({ ...f, ota_buy_prices: { ...(f.ota_buy_prices || {}), [e.id]: ev.target.value } }))} className="bg-amber-50" data-testid={`et-buyprice-${e.eticket_number}`}/>
                  </div>
                  <div className="col-span-1 sm:col-span-1 text-center">
                    <Label className="text-xs">Pax</Label>
                    <div className="mono font-semibold text-slate-800 mt-2">{pax}</div>
                  </div>
                  <div className="col-span-1 sm:col-span-1 text-right">
                    <Label className="text-xs">Line Total</Label>
                    <div className="mono font-bold text-[#556b2f] text-sm">{rupiah(price * pax)}</div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle>Detail Invoice</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label>Tanggal Invoice</Label><Input type="date" value={form.invoice_date} onChange={(e) => set("invoice_date", e.target.value)} data-testid="invoice-date" /></div>
            <div>
              <Label>Payment Status</Label>
              <Select value={form.payment_status} onValueChange={(v) => set("payment_status", v)}>
                <SelectTrigger data-testid="payment-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Unpaid">Unpaid</SelectItem>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Paid">Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Booking Source</Label>
              <Select value={form.booking_source} onValueChange={(v) => set("booking_source", v)}>
                <SelectTrigger data-testid="booking-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Agoda","Trip.com","Traveloka","Tiket.com","Airline Direct","Other"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Payment Method</Label><Input value={form.payment_method} onChange={(e) => set("payment_method", e.target.value)} placeholder="BCA 123 / Transfer / Cash" /></div>

            <div className="col-span-1 sm:col-span-2 border-t pt-3">
              <Label className="text-slate-500 text-xs uppercase tracking-wider">Info dari E-ticket (read-only)</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2 text-sm">
                <div><span className="text-slate-500">Airline:</span> <span className="font-medium">{form.airline || "-"}</span></div>
                <div><span className="text-slate-500">PNR:</span> <span className="mono font-medium">{form.pnr || "-"}</span></div>
                <div className="sm:col-span-2"><span className="text-slate-500">Route:</span> <span className="font-medium">{form.route || "-"}</span></div>
                <div className="sm:col-span-2"><span className="text-slate-500">Passenger:</span> <span className="font-medium">{form.passenger || "-"}</span></div>
              </div>
            </div>

            <div className="col-span-1 sm:col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Ringkasan Harga</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {!useLineItems && (
              <>
                <div><Label>Ticket Price (per pax)</Label><Input type="number" value={form.ticket_price} onChange={(e) => set("ticket_price", e.target.value)} data-testid="ticket-price"/></div>
              </>
            )}
            <div>
              <Label>Jumlah Penumpang (pax)</Label>
              <Input type="number" min="1" value={form.pax_count} onChange={(e) => set("pax_count", e.target.value)} data-testid="pax-count"/>
              {useLineItems && <p className="text-[10px] text-slate-400 mt-1">Info: pax otomatis dari e-ticket, boleh diubah manual.</p>}
            </div>
            <div><Label>Service Fee</Label><Input type="number" value={form.service_fee} onChange={(e) => set("service_fee", e.target.value)} data-testid="service-fee"/></div>
            <div><Label>Other Fee</Label><Input type="number" value={form.other_fee} onChange={(e) => set("other_fee", e.target.value)} data-testid="other-fee"/></div>
            <div><Label className="text-red-700">Cashback / Diskon</Label><Input type="number" value={form.cashback} onChange={(e) => set("cashback", e.target.value)} data-testid="cashback"/></div>

            <div className="border-t pt-3 space-y-1">
              {useLineItems && <div className="flex justify-between text-xs text-slate-500"><span>Total E-ticket ({selectedEts.length})</span><span className="mono">{rupiah(linesTotal)}</span></div>}
              <div className="flex justify-between text-xs text-slate-500"><span>Subtotal</span><span className="mono">{rupiah(subtotal)}</span></div>
              {Number(form.cashback) > 0 && (
                <div className="flex justify-between text-xs text-red-600"><span>Cashback</span><span className="mono">-{rupiah(form.cashback)}</span></div>
              )}
              <div className="flex items-center justify-between pt-2">
                <span className="text-slate-500 text-sm font-semibold">TOTAL</span>
                <span className="text-2xl font-extrabold text-[#556b2f] mono" data-testid="invoice-total">{rupiah(total)}</span>
              </div>
            </div>
            {publicUrl && (
              <div className="pt-3 border-t">
                <Label className="text-xs">Link publik untuk customer (di QR)</Label>
                <Input readOnly value={publicUrl} className="text-xs mt-1 mono" data-testid="invoice-public-url"/>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
